import { Box, Grid, List, ListItem, Stack, SxProps, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { ElementType, MouseEvent as ReactMouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { FOOTER_HEIGHT } from '../../constants/layout';
import { ReactLink } from '../../models/common';
import { StrapiPiedPageEntity } from '../../models/layout';
import { transformLinks } from '../../utils/tools';
import { Button } from '../atoms/Button';
import { IconButton } from '../atoms/IconButton';
import { ImageStrapi } from '../atoms/ImageStrapi';
import { Link } from '../atoms/Link';
import { IconFacebook } from '../icons/IconFacebook';
import { IconLinkedin } from '../icons/IconLinkedin';
import { IconYoutube } from '../icons/IconYoutube';

import { useSocialReport } from '../../hooks/analytic/useSocialReport';
import { showDidomiPreferences } from '../../utils/didomi';
import { Container } from './Container';

export type FooterProps = {
  piedPage?: StrapiPiedPageEntity;
};

const LinkFooter = ({
  label,
  isCurrentPage,
  linkProps
}: {
  label: string;
  isCurrentPage: boolean;
  linkProps: {
    to?: string;
    component?: ElementType;
    href?: string;
    target?: '_blank';
    onClick?: (event: ReactMouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  };
}) => (
  <Link
    key={label}
    {...linkProps}
    {...(isCurrentPage ? { 'aria-current': 'page' } : {})}
    sx={{
      color: 'white',
      textDecoration: 'none',
      fontWeight: 600,
      fontSize: isCurrentPage ? '18px' : '1rem',
      textAlign: { xs: 'center', md: 'left' },
      '&:hover': {
        textDecoration: 'underline'
      }
    }}
  >
    {label}
  </Link>
);

const buildFooterLinks = (reactLinks: ReactLink[], currentPathname: string) =>
  reactLinks.map(({ label, href, to }) => {
    const isCurrentPage = Boolean(to && currentPathname === to);
    return (
      <ListItem key={label} sx={{ px: 0 }} {...(isCurrentPage ? { 'aria-current': true } : {})}>
        <LinkFooter
          label={label}
          isCurrentPage={isCurrentPage}
          linkProps={to ? { to, component: RouterLink } : { href, target: '_blank' }}
        />
      </ListItem>
    );
  });

const IconReseauSocial = ({ reseauSocial }: { reseauSocial: string }) => {
  switch (reseauSocial) {
    case 'LinkedIn':
      return <IconLinkedin />;
    case 'Facebook':
      return <IconFacebook />;
    case 'YouTube':
      return <IconYoutube />;
    default:
      return <IconLinkedin />;
  }
};

const getSocialEvent = (label: string): string => {
  switch (label) {
    case 'LinkedIn':
      return 'social_linkedin';
    case 'Facebook':
      return 'social_facebook';
    case 'YouTube':
      return 'social_youtube';
    default:
      throw new Error('Invalid social network');
  }
};

export const Footer = ({ piedPage }: FooterProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [socialEvent, setSocialEvent] = useState<string>('');
  useSocialReport({ event: socialEvent });

  if (!piedPage) return null;

  const leftLinks = transformLinks(piedPage.LiensAGauche ?? []);
  const rightLinks = transformLinks(piedPage.LiensADroite ?? []);
  const mediaLinks = transformLinks(piedPage.LiensReseauxSociaux ?? []);

  const footerLinksSx: SxProps = {
    svg: { color: 'secondary.main' },
    '.MuiSvgIcon-root': {
      height: '1rem',
      width: '1rem'
    }
  };

  return (
    <Box
      component="footer"
      sx={{
        display: 'flex',
        alignItems: 'center',
        minHeight: FOOTER_HEIGHT,
        backgroundColor: 'secondary.main'
      }}
      role="contentinfo"
    >
      <Container>
        <Grid
          container
          columnSpacing={1}
          sx={{
            alignItems: { xs: 'center', md: 'stretch' },
            flexDirection: { xs: 'column', md: 'row' },
            py: '2.5rem'
          }}
        >
          <Grid item xs={12} md={3} sx={{ mb: { xs: '1.5rem', md: 0 } }}>
            <ImageStrapi
              strapiImage={piedPage.Logo}
              alt="PILEJE Laboratoire, nourrir la santé qui est en nous."
              selectedFormat="thumbnail"
              sx={{ width: 'auto', maxHeight: '8rem' }}
            />
            <Link href="https://www.bcorp.com/">
              <ImageStrapi
                strapiImage={piedPage.LogoBCorp}
                alt="PILEJE, entreprise certifiée B-CORP"
                selectedFormat="thumbnail"
                sx={{ width: 'auto', maxHeight: '8rem' }}
              />
            </Link>
            {piedPage.LogoCalliope && (
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Link
                  href="https://viewer.joomag.com/certificat-pileje-qualiopi-2025-2028/0051058001767628901"
                  sx={{ pl: { md: '10px' } }}
                >
                  <ImageStrapi
                    strapiImage={piedPage.LogoCalliope}
                    alt="Groupe Calliope - Certificat Qualiopi"
                    selectedFormat="thumbnail"
                    sx={{ width: 'auto', maxHeight: '5rem' }}
                  />
                </Link>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={2.5}>
            <List>{buildFooterLinks(leftLinks, pathname)}</List>
          </Grid>
          <Grid item xs={12} md={2.5}>
            <List>
              {buildFooterLinks(rightLinks, pathname)}
              <ListItem sx={{ px: 0 }}>
                <Button
                  sx={{
                    m: 0,
                    p: 0,
                    borderRadius: 0,
                    fontFamily: 'Open Sans Regular, Arial, sans-serif',
                    color: 'white',
                    textDecoration: 'none',
                    fontWeight: 600,
                    textAlign: { xs: 'center', md: 'left' },
                    '&:hover': {
                      textDecoration: 'underline'
                    },
                    '&:focus, &:focus-visible': {
                      outline: 'auto',
                      outlineColor: '-webkit-focus-ring-color',
                      outlineWidth: '1px',
                      boxShadow: 'none',
                      transition: 'transparent 0.2s ease'
                    }
                  }}
                  disableRipple
                  onClick={showDidomiPreferences}
                >
                  {t('footer.cookie-consent-choices')}
                </Button>
              </ListItem>
            </List>
          </Grid>
          <Grid item xs={12} md={4} sx={{ mt: { xs: '1.5rem', md: 0 } }}>
            <Stack>
              <Stack
                sx={{
                  flexDirection: 'row',
                  alignSelf: { xs: 'center', md: 'flex-end' },
                  columnGap: 1.25
                }}
              >
                {mediaLinks.map((link) => (
                  <IconButton
                    key={link.label}
                    href={link.href}
                    target="_blank"
                    size="small"
                    iconButtonType="white"
                    sx={footerLinksSx}
                    disableRipple
                    onClick={() => setSocialEvent(getSocialEvent(link.label))}
                  >
                    <IconReseauSocial reseauSocial={link.label} />
                    <span style={visuallyHidden}>{link.label}</span>
                  </IconButton>
                ))}
              </Stack>
              <Typography
                variant="h3"
                sx={{
                  pt: '2rem',
                  pb: '1.25rem',
                  color: 'white',
                  textAlign: { xs: 'center', md: 'right' }
                }}
              >
                {t('footer.fruggrScore')} : {piedPage.ScoreFruggr}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'white',
                  textAlign: { xs: 'center', md: 'right' }
                }}
              >
                © {new Date().getFullYear()} {t('footer.copyright')}
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};
